/**
 * CommercialCase.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Version "vente / pitch" de GenericCase.
 *
 * • Même moteur PESA, même store usePesa, même script backend — aucun changement API.
 * • Layer sémantique VOCAB_MAP : traduit les clés pédagogiques en vocabulaire commercial.
 * • UI "Pitch Room" : fond clair/ardoise, accents encre + or, switchable dark.
 * • Mode SOLO   → l'utilisateur pilote seul, génère son debrief (PDF/PPTX futur).
 * • Mode LIVE   → lien partageable, le client observe en temps réel.
 * • Tous les panneaux systémiques conservés mais redessinés (vocabulaire + couleurs).
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
} from 'chart.js';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  ArrowRight,
  BarChart2,
  BookOpen,
  Brain,
  CheckCircle2,
  ChevronLeft,
  Crosshair,
  FileText,
  GitBranch,
  HelpCircle,
  History,
  Layers,
  Lightbulb,
  Link2,
  Maximize2,
  Moon,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Presentation,
  Sun,
  Target,
  TrendingDown,
  TrendingUp,
  Users,
  X,
  Zap
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Radar } from 'react-chartjs-2';
import { createPortal } from 'react-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../config/axiosConfig';
import { usePesa } from '../stores/usePesaEngine';

// ─── Réutilisés depuis GenericCase ───────────────────────────────────────────
import CommercialBuilder from '../components/Simulations/CommercialCreator';
import CommercialOverlay from '../components/Simulations/CommercialOverlay';
import RapportEnquete from '../components/Simulations/RapportEnquete';
import {
  LiveFieldModal,
  PESAField
} from './GenericCase';


ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// ═══════════════════════════════════════════════════════════════════════════════
// 1. VOCAB MAP — traduction sémantique frontend uniquement
//    Chaque entrée : { label, sublabel? }
//    Usage : V('pedagogicalGoal') → "Proposition de valeur"
// ═══════════════════════════════════════════════════════════════════════════════

const VOCAB_MAPS = {
  vente: {
    pedagogicalGoal:      { label: 'Proposition de valeur',       icon: Target    },
    steps:                { label: 'Phases de la proposition',    icon: Layers    },
    action:               { label: 'Décisions stratégiques',      icon: Zap       },
    solutions:            { label: 'Scénarios de résultat',       icon: BarChart2 },
    counterfactuals:      { label: 'Et si vous aviez choisi…',    icon: GitBranch },
    causality:            { label: "Chaîne d'impact",             icon: Network   },
    prediction:           { label: 'Probabilité de succès',       icon: TrendingUp},
    history:              { label: 'Journal de décisions',        icon: History   },
    dynamics:             { label: 'Dynamiques en jeu',           icon: Activity  },
    theory:               { label: 'Références & Benchmark',      icon: BookOpen  },
    parameters:           { label: 'Indicateurs de performance',  icon: BarChart2 },
    whatHappened:         { label: 'Ce que votre client perçoit'              },
    whyItMatters:         { label: 'Pourquoi ça décide'                       },
    whatItCosts:          { label: 'Le risque si vous ne le faites pas'       },
    boostCondition:       { label: 'Le levier suivant'                        },
    competencySignal:     { label: 'Signal de confiance client'               },
    currentSolution:      { label: 'Scénario activé'                          },
    phase_observation:    { label: 'Analyse du contexte'                      },
    phase_action:         { label: 'Vos décisions'                            },
    phase_consequences:   { label: 'Impact projeté'                           },
    phase_tension:        { label: 'Friction du marché'                       },
    phase_bifurcation:    { label: 'Point de bascule'                         },
    stabilite:            { label: 'Stabilité du projet'                      },
    confiance:            { label: 'Confiance client'                         },
    ethique:              { label: 'Intégrité de l\'offre'                    },
    equite:               { label: 'Équité de valeur'                         },
    mediation:            { label: 'Capacité de dialogue'                     },
    innovation:           { label: 'Innovation'                               },
    santePublique:        { label: 'Impact marché'                            },
    sante:                { label: 'Santé du projet'                          },
  },
  pedagogique: {
    pedagogicalGoal:      { label: 'Objectif pédagogique',        icon: Brain     },
    steps:                { label: 'Étapes',                      icon: Layers    },
    action:               { label: 'Actions',                     icon: Zap       },
    solutions:            { label: 'Solutions',                   icon: CheckCircle2},
    counterfactuals:      { label: 'Contrefactuels',              icon: GitBranch },
    causality:            { label: 'Graphe causal',               icon: Network   },
    prediction:           { label: 'Prédictions',                 icon: Lightbulb },
    history:              { label: 'Journal',                     icon: History   },
    dynamics:             { label: 'Dynamiques',                  icon: Activity  },
    theory:               { label: 'Théories',                    icon: Brain     },
    parameters:           { label: 'Métriques système',           icon: BarChart2 },
    whatHappened:         { label: "Ce qui s'est passé"                       },
    whyItMatters:         { label: 'Pourquoi ça compte'                       },
    whatItCosts:          { label: 'Ce que ça coûte'                          },
    boostCondition:       { label: 'Levier suivant'                           },
    competencySignal:     { label: 'Compétence'                               },
    currentSolution:      { label: 'Solution atteinte'                        },
  },
};

// Hook vocabulaire : lit scenario.displayMode ou fallback 'vente'
const useVocab = (scenario) => {
  const mode = scenario?.displayMode || 'vente';
  const map  = VOCAB_MAPS[mode] || VOCAB_MAPS.vente;
  return (key) => map[key] || { label: key };
};

// ═══════════════════════════════════════════════════════════════════════════════
// 2. THÈME — palette switchable clair ↔ sombre
// ═══════════════════════════════════════════════════════════════════════════════

const THEMES = {
  light: {
    bg:           'bg-[#f8fafc]',
    bgHex:        '#f8fafc',
    surface:      'bg-white/80',
    surfaceHex:   '#FFFFFF',
    border:       'border-[#e2e8f0]',
    borderHex:    '#e2e8f0',
    text:         'text-[#0f172a]',
    textMuted:    'text-[#64748b]',
    accent:       '#f59e0b',        // ambre
    accentText:   'text-[#f59e0b]',
    accentBg:     'bg-[#f59e0b]/10',
    ink:          '#0f172a',
    panelBg:      'rgba(255,255,255,0.85)',
    panelBorder:  'rgba(245,158,11,0.15)',
    gridLine:     'rgba(15,23,42,0.04)',
    radarBg:      'rgba(245,158,11,0.04)',
    radarBorder:  '#f59e0b',
    headerBg:     'rgba(248,250,252,0.92)',
  },
  dark: {
    bg:           'bg-[#020617]',        // même que C.bg
    bgHex:        '#020617',
    surface:      'bg-[#0f172a]/90',     // slate-900
    surfaceHex:   '#0f172a',
    border:       'border-[#1e293b]',
    borderHex:    '#1e293b',
    text:         'text-[#f1f5f9]',
    textMuted:    'text-[#94a3b8]',
    accent:       '#f59e0b',             // ambre
    accentText:   'text-[#f59e0b]',
    accentBg:     'bg-[#f59e0b]/10',
    ink:          '#f1f5f9',
    panelBg:      'rgba(15,23,42,0.92)',
    panelBorder:  'rgba(245,158,11,0.15)',
    gridLine:     'rgba(255,255,255,0.03)',
    radarBg:      'rgba(245,158,11,0.04)',
    radarBorder:  '#f59e0b',
    headerBg:     'rgba(2,6,23,0.92)',
  },
};

// Lit scenario.theme ('light' | 'dark') ou fallback 'light'
const useCommercialTheme = (scenario) => {
  const [themeKey, setThemeKey] = useState(scenario?.theme || 'dark');
  const theme = THEMES[themeKey] || THEMES.light;
  const toggle = () => setThemeKey(k => k === 'light' ? 'dark' : 'light');
  return { theme, themeKey, toggle };
};

// ═══════════════════════════════════════════════════════════════════════════════
// 3. ATOMS / MICRO-COMPOSANTS
// ═══════════════════════════════════════════════════════════════════════════════

const PitchTag = ({ children, color = 'gold', theme }) => {
  const styles = {
    gold:    { bg: `${theme.accent}18`, border: `${theme.accent}30`, color: theme.accent },
    ink:     { bg: `${theme.ink}08`,    border: `${theme.ink}15`,    color: theme.ink    },
    green:   { bg: '#10b98118',         border: '#10b98130',         color: '#10b981'    },
    red:     { bg: '#ef444418',         border: '#ef444430',         color: '#ef4444'    },
    slate:   { bg: '#94a3b818',         border: '#94a3b830',         color: '#94a3b8'    },
  };
  const s = styles[color] || styles.gold;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[9px] font-mono tracking-widest uppercase"
      style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color }}
    >
      {children}
    </span>
  );
};

const Divider = ({ theme }) => (
  <div className="h-px w-full" style={{ background: theme.borderHex }} />
);

const SectionHeader = ({ label, icon: Icon, theme }) => (
  <div className="flex items-center gap-2 mb-3">
    {Icon && <Icon size={11} style={{ color: theme.accent }} />}
    <span
      className="text-[9px] font-mono tracking-[0.25em] uppercase"
      style={{ color: theme.accent }}
    >
      {label}
    </span>
  </div>
);

const PitchCard = ({ children, className = '', theme, style = {} }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: -6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -6 }}
    transition={{ duration: 0.2, ease: 'easeOut' }}
    className={`rounded-xl overflow-hidden backdrop-blur-md ${className}`}
    style={{
      background: theme.panelBg,
      border: `1px solid ${theme.panelBorder}`,
      ...style,
    }}
  >
    {children}
  </motion.div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// 4. PANNEAU — DYNAMIQUES (remplacé "Dynamiques" → "Dynamiques en jeu")
// ═══════════════════════════════════════════════════════════════════════════════

const CommercialDynamicsPanel = ({ theme, V }) => {
  const { engine } = usePesa();
  const dynamics    = engine?.case?.simulationEngine?.dynamics || [];
  const currentTick = engine?.currentTick || 0;
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setProgress(p => (p + 8) % 100), 600);
    return () => clearInterval(id);
  }, []);

  if (dynamics.length === 0) return null;

  return (
    <div className="p-4 space-y-3">
      <div
        className="flex items-center justify-between px-3 py-2.5 rounded-lg"
        style={{ background: `${theme.accent}08`, border: `1px solid ${theme.accent}18` }}
      >
        <div>
          <div className="text-[9px] font-mono tracking-widest uppercase mb-1.5" style={{ color: `${theme.accent}80` }}>
            Cycle de simulation
          </div>
          <div className="h-1 w-28 rounded-full overflow-hidden" style={{ background: `${theme.ink}08` }}>
            <motion.div
              className="h-full rounded-full"
              style={{ background: `linear-gradient(90deg, ${theme.accent}, ${theme.accent}80)`, width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
        <span className="text-sm font-mono font-bold tabular-nums" style={{ color: theme.accent }}>
          T-{String(currentTick).padStart(3, '0')}
        </span>
      </div>

      {dynamics.slice(0, 4).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {dynamics.slice(0, 4).map((d, i) => (
            <PitchTag key={i} color="gold" theme={theme}>{d.type || 'Processus'}</PitchTag>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 5. PANNEAU — PROBABILITÉ DE SUCCÈS (remplacé "Prédiction")
// ═══════════════════════════════════════════════════════════════════════════════

const CommercialPredictionPanel = ({ theme, V }) => {
  const { engine, phase, currentSolution } = usePesa();
  const [showInfo, setShowInfo] = useState(false);

  const getStatus = (pct) => {
    if (pct >= 90) return { text: 'Proposition exceptionnelle', color: '#10b981' };
    if (pct >= 75) return { text: 'Forte probabilité de closing', color: '#10b981' };
    if (pct >= 60) return { text: 'Chemin favorable',            color: theme.accent };
    if (pct >= 40) return { text: 'Issue incertaine',            color: '#f59e0b'   };
    return           { text: 'Proposition à retravailler',      color: '#ef4444'   };
  };

  let proba = 0;
  if (phase === 'finished' && currentSolution?.interpretation) {
    proba = currentSolution.interpretation.realWorldProbability ?? currentSolution.interpretation.theoreticalPromise ?? 10;
  } else {
    proba = engine?.state?.optimalPath?.probabilityOfSuccess ?? engine?.state?.prediction?.probability ?? 0;
  }
  const percentage = Math.max(0, Math.min(100, proba > 1 ? proba : proba * 100));
  const status     = getStatus(percentage);

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[9px] font-mono tracking-widest uppercase mb-1" style={{ color: `${theme.ink}40` }}>
            {V('prediction').label}
          </div>
          <span className="text-3xl font-bold font-mono tabular-nums" style={{ color: theme.ink }}>
            {percentage.toFixed(1)}
            <span className="text-lg" style={{ color: `${theme.ink}40` }}>%</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <PitchTag color={percentage >= 60 ? 'green' : percentage >= 40 ? 'gold' : 'red'} theme={theme}>
            {status.text}
          </PitchTag>
          <button onClick={() => setShowInfo(true)} style={{ color: `${theme.ink}30` }} className="hover:opacity-70 transition-opacity">
            <HelpCircle size={11} />
          </button>
        </div>
      </div>

      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: `${theme.ink}08` }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          className="h-full rounded-full"
          style={{ background: status.color, boxShadow: `0 0 8px ${status.color}50` }}
        />
      </div>

      <div className="text-[9px] font-mono tracking-widest uppercase" style={{ color: `${theme.ink}25` }}>
        {phase === 'finished' ? 'Données historiques réelles (1900–2025)' : 'Prédiction — optimisation génétique'}
      </div>

      {createPortal(
        <AnimatePresence>
          {showInfo && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
              style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}
              onClick={() => setShowInfo(false)}
            >
              <motion.div
                initial={{ scale: 0.96, y: 8 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 8 }}
                onClick={e => e.stopPropagation()}
                className="max-w-md w-full rounded-2xl p-7 shadow-2xl"
                style={{ background: theme.surfaceHex, border: `1px solid ${theme.panelBorder}` }}
              >
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-base font-semibold" style={{ color: theme.ink }}>Calibration bayésienne</h3>
                    <div className="text-[9px] font-mono tracking-widest mt-0.5" style={{ color: theme.accent }}>Moteur PESA</div>
                  </div>
                  <button onClick={() => setShowInfo(false)} style={{ color: `${theme.ink}40` }} className="p-1.5 rounded-lg hover:opacity-70 transition-opacity">
                    <X size={16} />
                  </button>
                </div>
                <div className="space-y-3 text-[12px] leading-relaxed" style={{ color: `${theme.ink}70` }}>
                  <p>Ce pourcentage n'est pas une promesse. C'est la <span className="font-semibold" style={{ color: theme.ink }}>fréquence historique réelle</span> de trajectoires similaires (1900–2025).</p>
                  <div className="px-4 py-3 rounded-lg text-[11px] italic" style={{ background: `${theme.accent}08`, border: `1px solid ${theme.accent}20`, color: theme.accent }}>
                    Exemple : une proposition similaire réussit à 95 % en simulation — mais dans le monde réel, 78 % ont été acceptées → probabilité réelle ≈ 74 %.
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 6. PANNEAU — JOURNAL DE DÉCISIONS (remplacé "Historique")
// ═══════════════════════════════════════════════════════════════════════════════

const CommercialHistoryPanel = ({ theme, V }) => {
  const { history } = usePesa();
  return (
    <div className="flex flex-col max-h-[260px]">
      <div className="flex-1 overflow-y-auto px-3 py-2.5 space-y-1">
        {history.slice().reverse().map((h, i) => (
          <div key={i} className="flex items-start gap-2.5 px-2.5 py-2 rounded-lg transition-colors group"
            style={{ ':hover': { background: `${theme.ink}04` } }}
          >
            <div className="shrink-0 w-0.5 self-stretch rounded-full mt-0.5" style={{ background: `${theme.accent}40` }} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[9px] font-mono tracking-widest uppercase" style={{ color: `${theme.accent}60` }}>
                  {new Date().toLocaleTimeString().slice(3, 8)}
                </span>
                <span className="text-[9px] font-mono font-semibold" style={{ color: `${theme.ink}70` }}>{h.type}</span>
              </div>
              <p className="text-[9px] font-mono italic truncate" style={{ color: `${theme.ink}30` }}>
                {h.actionId || 'Shift interne'}
              </p>
            </div>
          </div>
        ))}
        {history.length === 0 && (
          <div className="text-center py-8">
            <span className="text-[9px] font-mono tracking-widest uppercase" style={{ color: `${theme.ink}20` }}>
              Aucune décision enregistrée
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 7. PANNEAU — SCÉNARIO ACTIVÉ (remplacé "Solutions")
// ═══════════════════════════════════════════════════════════════════════════════

const CommercialSolutionsPanel = ({ theme, V }) => {
  const { currentSolution, phase } = usePesa();
  const isPlaying = phase === 'playing' || phase === 'loading';

  return (
    <div className="p-4 space-y-3">
      {isPlaying ? (
        <div className="space-y-2.5">
          <div className="text-[9px] font-mono tracking-widest uppercase" style={{ color: `${theme.ink}30` }}>
            Calcul du scénario optimal…
          </div>
          <div className="flex gap-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-0.5 flex-1 rounded-full animate-pulse" style={{ background: `${theme.accent}30`, animationDelay: `${i * 100}ms` }} />
            ))}
          </div>
        </div>
      ) : currentSolution ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2.5">
            <CheckCircle2 size={13} style={{ color: '#10b981' }} className="shrink-0" />
            <span className="text-sm font-semibold leading-tight" style={{ color: theme.ink }}>{currentSolution.name}</span>
            <PitchTag color="green" theme={theme}>VALIDÉ</PitchTag>
          </div>
          <p className="text-[11px] leading-relaxed line-clamp-2" style={{ color: `${theme.ink}50` }}>
            {currentSolution.description}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="px-3 py-2 rounded-lg" style={{ background: `${theme.ink}04`, border: `1px solid ${theme.ink}08` }}>
              <div className="text-[9px] font-mono tracking-widest uppercase mb-0.5" style={{ color: `${theme.ink}30` }}>Complexité</div>
              <div className="text-[11px] font-mono" style={{ color: `${theme.ink}70` }}>{currentSolution.difficulty}</div>
            </div>
            <div className="px-3 py-2 rounded-lg" style={{ background: '#10b98108', border: '1px solid #10b98120' }}>
              <div className="text-[9px] font-mono tracking-widest uppercase mb-0.5" style={{ color: `${theme.ink}30` }}>Fiabilité</div>
              <div className="text-[11px] font-mono" style={{ color: '#10b981' }}>94%</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg" style={{ background: '#ef444408', border: '1px solid #ef444420' }}>
          <Activity size={11} className="animate-pulse shrink-0" style={{ color: '#ef4444' }} />
          <span className="text-[9px] font-mono tracking-widest uppercase" style={{ color: '#ef4444' }}>
            Scénario non encore atteint
          </span>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 8. ROI PANEL — colonne droite (remplace RadialParameters)
// ═══════════════════════════════════════════════════════════════════════════════

const ROIPanel = ({ theme, V, scenario }) => {
  const { currentSolution, phase, currentCase, parameters, initialParameters } = usePesa();

  const displayValues = useMemo(() => {
    if (Object.keys(parameters).length > 0 && Object.values(parameters).every(v => typeof v === 'number' && !isNaN(v))) {
      return { ...parameters };
    }
    return { ...initialParameters };
  }, [parameters, initialParameters]);

  const { keys, values, labels } = useMemo(() => {
    const extractedKeys = Object.keys(displayValues).filter(k => typeof displayValues[k] === 'number' && !isNaN(displayValues[k]));
    return {
      keys: extractedKeys,
      values: extractedKeys.map(k => displayValues[k] ?? 50),
      labels: extractedKeys.map(k => {
        const mapped = V(k);
        return mapped?.label || k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
      }),
    };
  }, [displayValues, V]);

  const deltaInfo = useMemo(() => {
    if (!currentSolution?.expectedMetrics || phase !== 'finished') return null;
    const expected = currentSolution.expectedMetrics;
    const valid    = keys.filter(k => expected[k] !== undefined);
    if (valid.length === 0) return null;
    const avgE = Math.round(valid.reduce((s, k) => s + (expected[k] || 0), 0) / valid.length);
    const avgC = Math.round(valid.reduce((s, k) => s + (displayValues[k] || 50), 0) / valid.length);
    return { delta: avgC - avgE, avgExpected: avgE, avgCurrent: avgC };
  }, [currentSolution, phase, keys, displayValues]);

  const status = !deltaInfo
    ? { label: 'EN COURS',        color: theme.accent }
    : deltaInfo.delta >= 10
    ? { label: 'SURPERFORMANCE',  color: '#10b981'    }
    : deltaInfo.delta < 0
    ? { label: 'SOUS OBJECTIF',   color: '#ef4444'    }
    : { label: 'DANS LES CLOUS',  color: theme.accent };

  const radarData = useMemo(() => ({
    labels,
    datasets: [{
      label: 'Spectre actuel',
      data: values,
      backgroundColor: `${status.color}08`,
      borderColor: status.color,
      borderWidth: 1.5,
      pointBackgroundColor: status.color,
      pointBorderColor: 'rgba(255,255,255,0.3)',
      pointBorderWidth: 1,
      pointRadius: 3,
      pointHoverRadius: 5,
      tension: 0.3,
    }],
  }), [labels, values, status.color]);

  const radarOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    animation: { duration: 500, easing: 'easeOutQuart' },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: theme.surfaceHex,
        titleFont: { family: '"DM Mono", monospace', size: 10 },
        bodyFont:  { family: '"DM Mono", monospace', size: 10 },
        padding: 10,
        borderColor: theme.panelBorder,
        borderWidth: 1,
        titleColor: theme.ink,
        bodyColor: theme.ink,
        callbacks: { label: (ctx) => ` ${ctx.parsed.r}%` },
      },
    },
    scales: {
      r: {
        angleLines: { color: `${theme.ink}06` },
        grid:       { color: `${theme.ink}08`, lineWidth: 0.5 },
        pointLabels: {
          color: `${theme.ink}60`,
          font:  { size: 9, family: '"DM Mono", monospace', weight: '400' },
          padding: 8,
        },
        ticks:      { display: false, stepSize: 25 },
        min: 0, max: 100, beginAtZero: true,
      },
    },
  }), [theme]);

  if (values.length === 0) return null;

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: theme.panelBg, border: `1px solid ${theme.panelBorder}` }}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <SectionHeader label={V('parameters').label} icon={BarChart2} theme={theme} />
            <h2 className="text-sm font-semibold tracking-tight" style={{ color: theme.ink }}>
              Analyse Spectrale
            </h2>
          </div>
          <div className="px-2 py-1 rounded text-[9px] font-mono tracking-widest" style={{ background: `${theme.ink}06`, border: `1px solid ${theme.ink}08`, color: `${theme.accent}` }}>
            {currentCase?.slug?.toUpperCase() || '—'}
          </div>
        </div>

        {/* Radar */}
        <div className="flex justify-center mb-4">
          <div className="w-full max-w-[280px]">
            <Radar data={radarData} options={radarOptions} />
          </div>
        </div>

        {/* Status row */}
        <div className="flex items-center justify-between px-3 py-2.5 rounded-lg mb-3" style={{ background: `${theme.ink}04`, border: `1px solid ${theme.ink}06` }}>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0" style={{ background: status.color }} />
            <div>
              <div className="text-[8px] font-mono uppercase tracking-widest mb-0.5" style={{ color: `${theme.ink}30` }}>Statut</div>
              <div className="text-[10px] font-mono font-semibold" style={{ color: status.color }}>{status.label}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[8px] font-mono uppercase tracking-widest mb-0.5" style={{ color: `${theme.ink}30` }}>Variance</div>
            <div className="text-lg font-bold font-mono tabular-nums" style={{ color: deltaInfo?.delta < 0 ? '#ef4444' : '#10b981' }}>
              {deltaInfo ? `${deltaInfo.delta > 0 ? '+' : ''}${deltaInfo.delta}%` : '—'}
            </div>
          </div>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-2 gap-2">
          {keys.slice(0, 8).map((k, i) => {
            const val       = displayValues[k] ?? 0;
            const pct       = Math.min(val, 100);
            const initVal   = initialParameters?.[k];
            const delta     = initVal !== undefined ? val - initVal : null;
            const deltaAbs  = delta !== null ? Math.abs(Math.round(delta)) : null;
            const deltaColor = delta === null ? '' : delta > 0 ? '#10b981' : delta < 0 ? '#ef4444' : `${theme.ink}40`;
            const deltaSymbol = delta === null ? '' : delta > 0 ? '↑' : delta < 0 ? '↓' : '•';

            return (
              <div key={k} className="relative overflow-hidden rounded-lg" style={{ background: `${theme.ink}04`, border: `1px solid ${theme.ink}07` }}>
                <div
                  className="absolute inset-y-0 left-0 rounded-l-lg"
                  style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${theme.accent}25, ${theme.accent}10)`, transition: 'width 0.7s cubic-bezier(0.2,0.9,0.4,1.1)' }}
                />
                <div className="relative flex items-center justify-between gap-2 px-3 py-2.5">
                  <div className="flex items-center gap-1.5 overflow-hidden">
                    <span className="text-[9px] font-mono font-medium uppercase tracking-wide truncate" style={{ color: `${theme.ink}65` }}>
                      {labels[i]}
                    </span>
                    {delta !== null && (
                      <span className="text-[8px] font-mono tabular-nums font-medium" style={{ color: deltaColor }}>
                        {deltaSymbol} {deltaAbs}%
                      </span>
                    )}
                  </div>
                  <span className="text-[12px] font-mono font-bold tabular-nums" style={{ color: theme.ink }}>
                    {val}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Cible solution */}
        {currentSolution && phase === 'finished' && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
            className="mt-3 flex items-center gap-2.5 px-3 py-2.5 rounded-lg"
            style={{ background: `${theme.accent}06`, border: `1px dashed ${theme.accent}30` }}
          >
            <Target size={11} style={{ color: theme.accent }} className="shrink-0" />
            <div>
              <span className="text-[8px] font-mono uppercase tracking-widest block mb-0.5" style={{ color: `${theme.ink}30` }}>Cible</span>
              <span className="text-[10px] font-mono font-semibold uppercase" style={{ color: theme.accent }}>
                {currentSolution.name}
              </span>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 9. PANNEAU — CHAÎNE D'IMPACT (remplacé "Causal")
// ═══════════════════════════════════════════════════════════════════════════════

const CommercialCausalPanel = ({ theme, V }) => {
  const [activeTab, setActiveTab]   = useState('impact');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { engine, completedActions } = usePesa();

  const completedArr  = completedActions instanceof Set ? [...completedActions] : [];
  const lastActionId  = completedArr[completedArr.length - 1] || null;
  const lastAction    = useMemo(() => {
    if (!lastActionId) return null;
    return engine?.case?.steps?.flatMap(s => s.actions || []).find(a => a.id === lastActionId) || null;
  }, [lastActionId, engine?.case?.steps]);

  const currentStep  = engine?.getCurrentStep?.();
  const globalCausal = engine?.case?.simulationEngine?.causality || [];
  const stepCausal   = currentStep?.causality || [];
  const causalities  = [...stepCausal, ...globalCausal].slice(0, 6);
  const df           = lastAction?.deepFeedback;
  const cc           = lastAction?.causalChain || [];
  const comp         = lastAction?.competencySignal;
  const hasFeedback  = df && (df.whatHappened || df.whyItMatters || df.whatItCosts);

  const tabs = [
    { id: 'impact',    label: V('causality').label,       icon: Network    },
    { id: 'feedback',  label: 'Analyse',                  icon: Zap,        disabled: !hasFeedback },
    { id: 'signal',    label: V('competencySignal').label, icon: Target,     disabled: !comp },
  ];

  const CausalContent = () => (
    <div className="space-y-3 p-4">
      {lastAction && (
        <div className="flex items-center gap-2">
          <div className="w-1 h-1 rounded-full" style={{ background: theme.accent }} />
          <span className="text-[9px] font-mono tracking-widest uppercase truncate" style={{ color: `${theme.accent}60` }}>
            ↳ {lastAction.id}
          </span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            disabled={tab.disabled}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[9px] font-mono tracking-wider uppercase transition-all border"
            style={{
              opacity: tab.disabled ? 0.2 : 1,
              cursor: tab.disabled ? 'not-allowed' : 'pointer',
              background: activeTab === tab.id ? `${theme.accent}12` : 'transparent',
              borderColor: activeTab === tab.id ? `${theme.accent}30` : `${theme.ink}10`,
              color: activeTab === tab.id ? theme.accent : `${theme.ink}40`,
            }}
          >
            <tab.icon size={9} />
            {tab.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'impact' && (
          <motion.div key="impact" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-1.5">
            {causalities.length > 0 ? causalities.map((c, i) => (
              <div key={i} className="px-3 py-2.5 rounded-lg" style={{ background: `${theme.ink}03`, border: `1px solid ${theme.ink}06` }}>
                <div className="flex items-center gap-2 text-[10px] mb-1.5">
                  <span className="truncate flex-1" style={{ color: `${theme.ink}50` }}>{c.cause}</span>
                  <ArrowRight size={8} style={{ color: `${theme.ink}20` }} className="shrink-0" />
                  <span className={`font-medium flex-1 text-right truncate`} style={{ color: (c.weight || 0) > 0 ? '#10b981' : '#ef4444' }}>
                    {c.effect}
                  </span>
                </div>
                <div className="h-0.5 w-full rounded-full overflow-hidden" style={{ background: `${theme.ink}08` }}>
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.abs((c.weight || 0) * 100)}%`,
                      background: (c.weight || 0) > 0 ? '#10b981' : '#ef4444',
                      opacity: 0.7,
                    }}
                  />
                </div>
                {c.evidence?.source && (
                  <p className="text-[9px] font-mono mt-1 truncate" style={{ color: `${theme.ink}20` }}>src: {c.evidence.source}</p>
                )}
              </div>
            )) : (
              <p className="text-center py-5 text-[9px] font-mono italic" style={{ color: `${theme.ink}20` }}>Aucun impact causal actif</p>
            )}

            {cc.length > 0 && (
              <div className="mt-2 pt-2 space-y-1.5" style={{ borderTop: `1px solid ${theme.ink}08` }}>
                <div className="text-[9px] font-mono tracking-widest uppercase mb-1.5" style={{ color: `${theme.ink}25` }}>
                  Chaîne d'impact
                </div>
                {cc.map((link, i) => (
                  <div key={i} className="flex items-start gap-2 px-2.5 py-2 rounded-lg" style={{ background: `${theme.ink}03` }}>
                    <Crosshair size={8} style={{ color: `${theme.accent}40` }} className="shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-[10px]" style={{ color: `${theme.ink}55` }}>{link.from} → {link.to}</p>
                      {link.mechanism && <p className="text-[10px] italic mt-0.5" style={{ color: `${theme.ink}30` }}>{link.mechanism}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'feedback' && hasFeedback && (
          <motion.div key="feedback" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2">
            <div className="px-3 py-2 rounded-lg" style={{ background: `${theme.ink}04` }}>
              <div className="text-[9px] font-mono tracking-widest uppercase mb-0.5" style={{ color: `${theme.ink}25` }}>Dernière décision</div>
              <p className="text-[11px] font-medium" style={{ color: theme.accent }}>{lastAction?.label}</p>
            </div>

            {[
              { key: 'whatHappened', icon: Activity,     colorKey: '#ink70'    },
              { key: 'whyItMatters', icon: BookOpen,     colorKey: theme.accent },
              { key: 'whatItCosts',  icon: TrendingDown, colorKey: '#ef4444'   },
            ].filter(({ key }) => df?.[key]).map(({ key, icon: Icon, colorKey }) => (
              <div key={key} className="p-3 rounded-lg space-y-1.5" style={{ border: `1px solid ${theme.ink}08`, background: `${theme.ink}02` }}>
                <div className="flex items-center gap-1.5">
                  <Icon size={9} style={{ color: colorKey === '#ink70' ? `${theme.ink}70` : colorKey }} />
                  <span className="text-[9px] font-mono tracking-widest uppercase" style={{ color: `${theme.ink}25` }}>
                    {V(key).label}
                  </span>
                </div>
                <p className="text-[11px] leading-relaxed" style={{ color: colorKey === '#ink70' ? `${theme.ink}70` : colorKey }}>
                  {df[key]}
                </p>
              </div>
            ))}

            {df?.boostCondition && (
              <div className="p-3 rounded-lg" style={{ background: '#10b98106', border: '1px solid #10b98120' }}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <TrendingUp size={9} style={{ color: '#10b981' }} />
                  <span className="text-[9px] font-mono tracking-widest uppercase" style={{ color: '#10b98170' }}>
                    {V('boostCondition').label}
                  </span>
                </div>
                <p className="text-[11px] italic leading-relaxed" style={{ color: '#10b98180' }}>{df.boostCondition}</p>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'signal' && comp && (
          <motion.div key="signal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2.5">
            <div className="p-4 rounded-xl" style={{ background: `${theme.accent}08`, border: `1px solid ${theme.accent}20` }}>
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <p className="text-sm font-semibold" style={{ color: theme.ink }}>{comp.name}</p>
                <PitchTag color="gold" theme={theme}>{comp.level}</PitchTag>
              </div>
              <p className="text-[11px] leading-relaxed" style={{ color: `${theme.ink}55` }}>{comp.descriptor}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <>
      <CausalContent />
      <div className="px-4 pb-4">
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-full py-2 rounded-lg text-[9px] font-mono tracking-widest uppercase transition-all duration-150"
          style={{ border: `1px solid ${theme.ink}10`, color: `${theme.ink}30` }}
          onMouseEnter={e => { e.currentTarget.style.background = `${theme.ink}04`; e.currentTarget.style.color = `${theme.ink}60`; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = `${theme.ink}30`; }}
        >
          Vue étendue →
        </button>
      </div>

      {createPortal(
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
              style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}
              onClick={() => setIsModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 12 }}
                onClick={e => e.stopPropagation()}
                className="max-w-2xl w-full max-h-[85vh] overflow-hidden rounded-2xl flex flex-col"
                style={{ background: theme.surfaceHex, border: `1px solid ${theme.panelBorder}`, boxShadow: '0 0 60px rgba(0,0,0,0.3)' }}
              >
                <div className="px-6 py-5 flex items-center justify-between shrink-0" style={{ borderBottom: `1px solid ${theme.borderHex}` }}>
                  <div>
                    <h2 className="text-base font-semibold" style={{ color: theme.ink }}>{V('causality').label}</h2>
                    <div className="text-[9px] font-mono tracking-widest mt-0.5" style={{ color: theme.accent }}>Vue complète</div>
                  </div>
                  <button onClick={() => setIsModalOpen(false)} className="p-1.5 rounded-lg transition-colors" style={{ color: `${theme.ink}40` }}>
                    <X size={16} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <CausalContent />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 10. PITCH SCREEN — remplace StartScreen, ton commercial
// ═══════════════════════════════════════════════════════════════════════════════

const PitchScreen = ({ onToggleTheme, scenario, engine, theme, V, themeKey, presentationMode, setPresentationMode, onStart, editorMode, setEditorMode }) => {
  const steps = scenario?.steps || [];
  const keyMetrics = scenario?.initialState?.parameters || {};

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center font-serif overflow-hidden"
      style={{ background: theme.bgHex }}
    >
      {/* Grid décorative */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `linear-gradient(${theme.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${theme.gridLine} 1px, transparent 1px)`,
        backgroundSize: '80px 80px',
      }} />

      {/* PESAField en fond, opacité réduite */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <PESAField />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        className="relative z-10 w-full max-w-5xl mx-6"
      >
        {/* Card principale */}
        <div className="rounded-2xl overflow-hidden" style={{ background: theme.panelBg, border: `1px solid ${theme.panelBorder}`, boxShadow: `0 32px 80px rgba(0,0,0,0.12)` }}>

          {/* Header strip */}
          <div className="px-10 py-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${theme.borderHex}`, background: `${theme.accent}06` }}>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: theme.accent }} />
              <span className="text-[9px] font-mono tracking-[0.3em] uppercase" style={{ color: theme.accent }}>
                Simulation commerciale — PESA Engine
              </span>
              <button
            onClick={() => setEditorMode(!editorMode)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs"
            style={{ borderColor: theme.borderHex, color: theme.ink }}
            >
            <Maximize2 size={14} /> Éditeur
            </button>
            </div>
            <div className="flex items-center gap-2">
              {/* Mode toggle */}
              <div className="flex items-center gap-1 p-1 rounded-lg" style={{ background: `${theme.ink}06`, border: `1px solid ${theme.ink}10` }}>
                {['solo', 'live'].map(m => (
                  <button
                    key={m}
                    onClick={() => setPresentationMode(m)}
                    className="px-3 py-1 rounded text-[9px] font-mono tracking-widest uppercase transition-all"
                    style={{
                      background: presentationMode === m ? theme.accent : 'transparent',
                      color: presentationMode === m ? '#fff' : `${theme.ink}40`,
                    }}
                  >
                    {m === 'solo' ? 'Solo' : 'Client Live'}
                  </button>
                ))}
                <button
                 onClick={onToggleTheme}
                 className="p-2.5 rounded-xl border transition-all"
                 style={{ background: `${theme.ink}04`, borderColor: theme.borderHex }}
                 >
                 {themeKey === 'dark' ? <Sun size={16} style={{ color: theme.ink }} /> : <Moon size={16} style={{ color: theme.ink }} />}
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

              {/* Gauche : titre + meta */}
              <div className="space-y-6">
                <div>
                  <div className="text-[9px] font-mono tracking-[0.3em] uppercase mb-3" style={{ color: `${theme.ink}30` }}>
                    {V('pedagogicalGoal').label}
                  </div>
                  <h1 className="text-4xl font-light leading-tight tracking-tight mb-4" style={{ color: theme.ink }}>
                    {scenario.title}
                  </h1>
                  <p className="text-[14px] leading-relaxed italic" style={{ color: `${theme.ink}55`, borderLeft: `2px solid ${theme.accent}`, paddingLeft: '1rem' }}>
                    "{scenario.pedagogicalGoal}"
                  </p>
                </div>

                {/* Étapes */}
                <div className="space-y-2">
                  <div className="text-[9px] font-mono tracking-[0.25em] uppercase mb-2" style={{ color: `${theme.ink}30` }}>
                    {V('steps').label} — {steps.length}
                  </div>
                  {steps.slice(0, 5).map((step, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-[9px] font-mono tabular-nums w-5 text-right" style={{ color: `${theme.accent}60` }}>
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <div className="h-px flex-1" style={{ background: `${theme.ink}10` }} />
                      <span className="text-[11px] font-mono truncate max-w-[220px]" style={{ color: `${theme.ink}55` }}>
                        {step.title}
                      </span>
                    </div>
                  ))}
                  {steps.length > 5 && (
                    <div className="text-[9px] font-mono" style={{ color: `${theme.ink}25`, paddingLeft: '2rem' }}>
                      + {steps.length - 5} phases supplémentaires
                    </div>
                  )}
                </div>

                {/* Métriques initiales */}
                <div>
                  <div className="text-[9px] font-mono tracking-[0.25em] uppercase mb-2" style={{ color: `${theme.ink}30` }}>
                    État initial du système
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(keyMetrics).slice(0, 6).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between px-3 py-1.5 rounded" style={{ background: `${theme.ink}04`, border: `1px solid ${theme.ink}06` }}>
                        <span className="text-[9px] font-mono uppercase truncate" style={{ color: `${theme.ink}50` }}>
                          {V(k).label || k}
                        </span>
                        <span className="text-[11px] font-mono font-bold tabular-nums" style={{ color: theme.accent }}>
                          {v}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Droite : CTA + mode info */}
              <div className="flex flex-col items-center justify-center gap-6">
                <div className="w-full space-y-3">
                  <button
                    onClick={onStart}
                    className="group w-full py-8 rounded-xl font-light text-xl flex items-center justify-center gap-4 transition-all duration-300"
                    style={{
                      background: theme.accent,
                      color: '#fff',
                      boxShadow: `0 8px 32px ${theme.accent}40`,
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <Presentation size={24} className="opacity-80" />
                    Lancer la simulation
                  </button>

                  <div
                    className="px-5 py-4 rounded-xl"
                    style={{ background: `${theme.ink}04`, border: `1px solid ${theme.ink}06` }}
                  >
                    {presentationMode === 'solo' ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <FileText size={11} style={{ color: theme.accent }} />
                          <span className="text-[10px] font-mono font-semibold" style={{ color: theme.ink }}>Mode Solo</span>
                        </div>
                        <p className="text-[11px] leading-relaxed" style={{ color: `${theme.ink}50` }}>
                          Pilotez la simulation, obtenez le debrief complet et exportez-le en PDF ou présentation.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Users size={11} style={{ color: theme.accent }} />
                          <span className="text-[10px] font-mono font-semibold" style={{ color: theme.ink }}>Mode Client Live</span>
                        </div>
                        <p className="text-[11px] leading-relaxed" style={{ color: `${theme.ink}50` }}>
                          Partagez le lien avec votre client. Vous pilotez, il observe en temps réel.
                        </p>
                        <div className="flex items-center gap-2 mt-2 px-2 py-1.5 rounded" style={{ background: `${theme.accent}10`, border: `1px solid ${theme.accent}20` }}>
                          <Link2 size={10} style={{ color: theme.accent }} />
                          <span className="text-[9px] font-mono truncate" style={{ color: `${theme.accent}80` }}>
                            {window.location.origin}/live/{scenario?.slug}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <p className="text-[10px] font-mono text-center" style={{ color: `${theme.ink}25` }}>
                  Simulation irréversible — les décisions ont des conséquences
                </p>
              </div>

            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 11. NAV BUTTON (version Pitch Room)
// ═══════════════════════════════════════════════════════════════════════════════

const PitchNavButton = ({ active, icon: Icon, label, onClick, pulsing = false, theme }) => (
  <button
    onClick={onClick}
    className="relative group flex items-center gap-2.5 w-full px-3 py-2.5 rounded-xl text-[11px] font-mono tracking-wider uppercase transition-all duration-200 border"
    style={{
      background: active ? `${theme.accent}12` : 'transparent',
      borderColor: active ? `${theme.accent}35` : `${theme.ink}10`,
      color: active ? theme.accent : `${theme.ink}40`,
    }}
    onMouseEnter={e => { if (!active) { e.currentTarget.style.background = `${theme.ink}04`; e.currentTarget.style.color = `${theme.ink}70`; }}}
    onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = `${theme.ink}40`; }}}
  >
    {pulsing && (
      <>
        <span className="absolute inset-0 rounded-xl animate-[ping_0.8s_ease-out_3]" style={{ border: `2px solid ${theme.accent}60` }} />
        <span className="absolute inset-0 rounded-xl" style={{ background: `${theme.accent}08` }} />
      </>
    )}
    <Icon size={13} />
    <span>{label}</span>
    {pulsing && <span className="ml-auto w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: theme.accent }} />}
  </button>
);

// ═══════════════════════════════════════════════════════════════════════════════
// 12. HEADER (version Pitch Room)
// ═══════════════════════════════════════════════════════════════════════════════

const CommercialHeader = ({ scenario, phase, progress, theme, themeKey, onToggleTheme, onRestart, presentationMode, setPresentationMode, navigate, editorMode, setEditorMode }) => (
  <header
    className="shrink-0 sticky top-0 z-[60] backdrop-blur-xl"
    style={{ background: theme.headerBg, borderBottom: `1px solid ${theme.borderHex}` }}
  >
    <div className="max-w-[1920px] mx-auto px-6 h-20 flex items-center gap-4">

      {/* Gauche */}
      <div className="flex items-center gap-5 flex-1">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2.5 rounded-xl border transition-all group"
          style={{ background: `${theme.ink}04`, borderColor: theme.borderHex }}
          onMouseEnter={e => e.currentTarget.style.background = `${theme.ink}08`}
          onMouseLeave={e => e.currentTarget.style.background = `${theme.ink}04`}
        >
          <ChevronLeft size={20} style={{ color: theme.ink }} />
        </button>

        
        

        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-widest"
              style={{ background: `${theme.accent}12`, color: theme.accent, border: `1px solid ${theme.accent}25` }}
            >
              Simulation
            </span>
            <h1 className="text-lg font-bold truncate tracking-tight" style={{ color: theme.ink }}>{scenario?.title}</h1>
          </div>
          <p className="text-[11px] truncate max-w-[300px] mt-0.5" style={{ color: `${theme.ink}40` }}>
            {scenario?.pedagogicalGoal}
          </p>
        </div>
      </div>

      {/* Centre */}
      <div className="flex-1 hidden md:flex flex-col items-center gap-2">
        <div className="flex items-center gap-3">
          <div
            className="flex items-center gap-2 px-3 py-1 rounded-full border text-[10px] font-bold tracking-tighter uppercase"
            style={{
              background: phase === 'playing' ? '#10b98112' : `${theme.accent}12`,
              borderColor: phase === 'playing' ? '#10b98130' : `${theme.accent}30`,
              color: phase === 'playing' ? '#10b981' : theme.accent,
            }}
          >
            <span className="relative flex h-2 w-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75`} style={{ background: phase === 'playing' ? '#10b981' : theme.accent }} />
              <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: phase === 'playing' ? '#10b981' : theme.accent }} />
            </span>
            {phase}
          </div>
          <div className="h-4 w-px" style={{ background: `${theme.ink}12` }} />
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: `${theme.ink}30` }}>Progression</span>
            <div className="w-32 h-1.5 rounded-full overflow-hidden" style={{ background: `${theme.ink}08`, border: `1px solid ${theme.ink}06` }}>
              <motion.div
                className="h-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.8, ease: 'circOut' }}
                style={{ background: `linear-gradient(90deg, ${theme.accent}, #10b981)` }}
              />
            </div>
            <span className="text-xs font-mono font-bold w-8" style={{ color: theme.ink }}>{progress}%</span>
          </div>
        </div>
      </div>

      {/* Droite */}
      <div className="flex items-center justify-end gap-3 flex-1">
        {/* Mode toggle */}
        <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: `${theme.ink}06`, border: `1px solid ${theme.ink}10` }}>
          {[
            { id: 'solo', icon: FileText, label: 'Solo' },
            { id: 'live', icon: Users,    label: 'Live' },
          ].map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => setPresentationMode(id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono tracking-wide transition-all"
              style={{
                background: presentationMode === id ? theme.accent : 'transparent',
                color: presentationMode === id ? '#fff' : `${theme.ink}40`,
              }}
            >
              <Icon size={12} />
              {label}
            </button>

            
          ))}
        </div>

        <button
          onClick={onToggleTheme}
          className="p-2.5 rounded-xl border transition-all"
          style={{ background: `${theme.ink}04`, borderColor: theme.borderHex }}
        >
          {themeKey === 'dark' ? <Sun size={16} style={{ color: theme.ink }} /> : <Moon size={16} style={{ color: theme.ink }} />}
        </button>

        <button
          onClick={onRestart}
          className="group flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-bold border transition-all"
          style={{ background: '#ef444410', color: '#ef4444', borderColor: '#ef444425' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#ef444410'; e.currentTarget.style.color = '#ef4444'; }}
        >
          <History size={14} />
          Réinitialiser
        </button>
      </div>
    </div>
  </header>
);

// ═══════════════════════════════════════════════════════════════════════════════
// 13. CONTREFACTUELS (version Pitch Room — "Et si vous aviez choisi…")
// ═══════════════════════════════════════════════════════════════════════════════

const CommercialCounterfactualsPanel = ({ counterfactuals = [], theme, V }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  if (!counterfactuals?.length) return null;
  const top3 = [...counterfactuals].sort((a, b) => b.deltaProbability - a.deltaProbability).slice(0, 3);

  return (
    <div className="p-4 space-y-3">
      <div className="space-y-2">
        {top3.map((cf, i) => (
          <div key={i} className="flex items-center gap-3 group">
            <div
              className="h-6 rounded shrink-0 flex items-center px-2"
              style={{ minWidth: `${Math.max(28, cf.deltaProbability * 100 * 0.6)}px`, background: `${theme.accent}10`, border: `1px solid ${theme.accent}20` }}
            >
              <span className="text-[9px] font-mono font-bold whitespace-nowrap" style={{ color: theme.accent }}>
                +{(cf.deltaProbability * 100).toFixed(0)}%
              </span>
            </div>
            <span className="text-[11px] truncate" style={{ color: `${theme.ink}55` }}>{cf.actionTitle}</span>
          </div>
        ))}
      </div>

      <button
        onClick={() => setIsModalOpen(true)}
        className="w-full py-2 rounded-lg text-[9px] font-mono tracking-widest uppercase transition-all duration-150"
        style={{ border: `1px solid ${theme.accent}20`, color: `${theme.accent}60` }}
        onMouseEnter={e => { e.currentTarget.style.background = `${theme.accent}08`; e.currentTarget.style.color = theme.accent; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = `${theme.accent}60`; }}
      >
        Analyse complète →
      </button>

      {createPortal(
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
              style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}
              onClick={() => setIsModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 12 }}
                onClick={e => e.stopPropagation()}
                className="max-w-2xl w-full max-h-[85vh] overflow-hidden rounded-2xl flex flex-col"
                style={{ background: theme.surfaceHex, border: `1px solid ${theme.panelBorder}`, boxShadow: '0 0 60px rgba(0,0,0,0.25)' }}
              >
                <div className="px-6 py-5 flex justify-between items-start shrink-0" style={{ borderBottom: `1px solid ${theme.borderHex}` }}>
                  <div>
                    <h2 className="text-base font-semibold" style={{ color: theme.ink }}>{V('counterfactuals').label}</h2>
                    <div className="text-[9px] font-mono tracking-widest mt-0.5" style={{ color: theme.accent }}>Bifurcation Engine</div>
                  </div>
                  <button onClick={() => setIsModalOpen(false)} style={{ color: `${theme.ink}40` }}><X size={16} /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {counterfactuals.map((cf, i) => (
                    <div key={i} className="p-5 rounded-xl" style={{ background: `${theme.ink}03`, border: `1px solid ${theme.ink}07` }}>
                      <div className="flex items-center gap-3 mb-3">
                        <PitchTag color="gold" theme={theme}>+{(cf.deltaProbability * 100).toFixed(1)}% impact</PitchTag>
                      </div>
                      <h4 className="text-sm font-medium mb-1.5" style={{ color: theme.ink }}>{cf.actionTitle}</h4>
                      <p className="text-[12px] leading-relaxed" style={{ color: `${theme.ink}55` }}>{cf.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 14. LOADING SCREEN (version Pitch Room)
// ═══════════════════════════════════════════════════════════════════════════════

const CommercialLoadingScreen = ({ theme }) => (
  <div className="fixed inset-0 z-[1000] flex items-center justify-center" style={{ background: theme.bgHex }}>
    <div className="flex flex-col items-center gap-8">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        className="w-20 h-20 rounded-full"
        style={{ border: `4px solid ${theme.accent}30`, borderTopColor: theme.accent }}
      />
      <span className="font-mono text-[10px] tracking-wide uppercase" style={{ color: `${theme.ink}40` }}>
        Initialisation du moteur PESA…
      </span>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// 15. PAGE PRINCIPALE — CommercialCase
// ═══════════════════════════════════════════════════════════════════════════════

export default function CommercialCase() {
  const {
    engine, loadSimulation, restartSimulation,
    toggleFloatingPanel, phase, scenario, currentStep,
    openPanels, closeDebrief, showDebrief, loadBuilderScenario,
    currentSolution, closeSolution, toggleShowDebrief,
  } = usePesa();

  const { slug }   = useParams();
  const navigate   = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);

  const [loading, setLoading]         = useState(true);
  const [leftCollapsed, setLeftCollapsed]   = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [presentationMode, setPresentationMode] = useState('solo'); // 'solo' | 'live'
  const [editorMode, setEditorMode] = useState(searchParams.get('editor') === 'true');

  // ── Vocabulaire et thème pilotés par le script ──────────────────────────────
  const V     = useVocab(scenario);
  const { theme, themeKey, toggle: toggleTheme } = useCommercialTheme(scenario);

  // ── Chargement ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchCase = async () => {
      try {
        const res = await api.get(`/cases/generic/${slug}`);
        loadSimulation(res.data);
        setLoading(false);
      } catch (err) {
        toast.error("Échec de l'initialisation");
        navigate('/dashboard');
      }
    };
    // Ne charge que si on a un slug ET qu'on n'est pas en mode éditeur
    if (slug && !editorMode) {
      fetchCase();
    } else if (editorMode) {
      // Pas de chargement, on affichera le builder directement
      setLoading(false);
    } else if (engine?.case) {
      setLoading(false);
    }
  }, [slug, editorMode, engine?.case, loadSimulation, navigate]);

  // ── Sync localStorage (même logique que GenericCase) ───────────────────────
  useEffect(() => {
    if (engine?.state && slug) {
      localStorage.setItem(`pesa_sync_${slug}`, JSON.stringify({
        state: engine.state,
        case: engine.case,
        scenario,
        lastUpdate: Date.now(),
      }));
    }
  }, [engine?.state, slug, scenario]);

  // ── Progression ─────────────────────────────────────────────────────────────
  const progress = useMemo(() => {
    if (!scenario?.steps?.length) return 0;
    const total   = scenario.steps.length;
    const current = engine?.state?.currentStepIndex || 0;
    return Math.min(100, Math.round((current / (total - 1)) * 100));
  }, [scenario, engine?.state?.currentStepIndex]);

  // ── Pulse panneaux sur action ────────────────────────────────────────────────
  const { completedActions } = usePesa();
  const [pulsingPanels, setPulsingPanels] = useState({});
  const prevCountRef = useRef(0);
  const completedArr = completedActions instanceof Set ? [...completedActions] : [];
  useEffect(() => {
    if (completedArr.length > prevCountRef.current) {
      const next = {};
      ['causal', 'prediction', 'counterfactuals', 'history'].forEach(p => { next[p] = Date.now(); });
      setPulsingPanels(next);
      prevCountRef.current = completedArr.length;
      const t = setTimeout(() => setPulsingPanels({}), 2400);
      return () => clearTimeout(t);
    }
  }, [completedArr.length]);
  const isPulsing = (panel) => !!pulsingPanels[panel];

  if (editorMode) {
  return (
    <CommercialBuilder
      slug={slug || 'nouveau-scenario'}
      onLaunch={(scenario) => {
        loadBuilderScenario(scenario);   // ← méthode sécurisée du store
        setEditorMode(false);
      }}
      onCancel={() => setEditorMode(false)}
      themeKey={themeKey}
      onToggleTheme={toggleTheme}
      theme={theme}
      V={V}
    />
  );
}

  // ── Guards ───────────────────────────────────────────────────────────────────
  if (loading || !scenario) return <CommercialLoadingScreen theme={theme} />;
  

  if (window.innerWidth < 1024) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center" style={{ background: theme.bgHex, color: theme.ink }}>
        <div className="max-w-md">
          <div className="text-6xl mb-6">💻</div>
          <h2 className="text-2xl font-bold mb-4">Simulation optimisée pour ordinateur</h2>
          <p className="mb-8" style={{ color: `${theme.ink}60` }}>Cette simulation est conçue pour un écran ≥ 1024px.</p>
          <button onClick={() => window.history.back()} className="px-6 py-3 rounded-xl border" style={{ borderColor: theme.borderHex, color: theme.ink }}>
            Retour
          </button>
        </div>
      </div>
    );
  }

  const handleStart = () => {
  if (engine.state.currentPhase?.duration > 0) {
    engine._schedulePhaseAdvance(engine.state.currentPhase.duration);
  }
  engine.state.isPhaseRunning = true;
  engine._notifyStore();
};

  return (
    <div
      className="min-h-screen font-sans selection:bg-amber-400/20 transition-colors duration-700"
      style={{ background: theme.bgHex, color: theme.ink }}
    >
      {/* Grid décorative */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{
        backgroundImage: `linear-gradient(${theme.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${theme.gridLine} 1px, transparent 1px)`,
        backgroundSize: '80px 80px',
      }} />

      {/* PESAField en fond global */}
      <div className="fixed inset-0 z-0 opacity-15">
        <PESAField />
      </div>

      <div className="relative z-10 flex flex-col h-screen">

        {/* ── PITCH SCREEN (avant démarrage) ────────────────────────────────── */}
        {engine && !engine.state.isPhaseRunning && (
          <PitchScreen
            scenario={scenario}
            engine={engine}
            theme={theme}
            V={V}
            presentationMode={presentationMode}
            setPresentationMode={setPresentationMode}
            onStart={handleStart}
            onToggleTheme={toggleTheme}
            setEditorMode={setEditorMode}
          />
        )}

        {/* ── HEADER ─────────────────────────────────────────────────────────── */}
        <CommercialHeader
          scenario={scenario}
          phase={phase}
          progress={progress}
          theme={theme}
          themeKey={themeKey}
          onToggleTheme={toggleTheme}
          onRestart={restartSimulation}
          presentationMode={presentationMode}
          setPresentationMode={setPresentationMode}
          navigate={navigate}
          setEditorMode={setEditorMode}
        />

        {/* ── GRID PRINCIPAL ──────────────────────────────────────────────────── */}
        <main
          className="flex-1 gap-0 overflow-hidden relative"
          style={{
            display: 'grid',
            gridTemplateColumns: [
              leftCollapsed  ? '0px' : 'minmax(0,1fr)',
              'minmax(0,2fr)',
              rightCollapsed ? '0px' : 'minmax(0,1fr)',
            ].join(' '),
            transition: 'grid-template-columns 350ms cubic-bezier(0.4,0,0.2,1)',
          }}
        >

          {/* ══ COLONNE GAUCHE ════════════════════════════════════════════════ */}
          <div
            className="flex flex-col gap-6 h-full overflow-hidden"
            style={{
              opacity:       leftCollapsed ? 0 : 1,
              pointerEvents: leftCollapsed ? 'none' : 'auto',
              transition:    'opacity 200ms ease',
              padding:       leftCollapsed ? 0 : '2rem',
              paddingRight:  leftCollapsed ? 0 : '1rem',
            }}
          >
            <div className="shrink-0 space-y-4">
              <div className="text-[10px] font-mono font-semibold uppercase tracking-wide px-2" style={{ color: `${theme.ink}30` }}>
                Panneaux
              </div>
              <div className="grid grid-cols-1 gap-2">
                {[
                  { key: 'dynamics',       Icon: Activity,   label: V('dynamics').label       },
                  { key: 'causal',         Icon: Network,    label: V('causality').label,     pulse: isPulsing('causal')          },
                  { key: 'history',        Icon: History,    label: V('history').label,       pulse: isPulsing('history')         },
                  { key: 'prediction',     Icon: TrendingUp, label: V('prediction').label,    pulse: isPulsing('prediction')      },
                  { key: 'counterfactuals',Icon: GitBranch,  label: V('counterfactuals').label,pulse: isPulsing('counterfactuals') },
                ].map(({ key, Icon, label, pulse }) => (
                  <PitchNavButton
                    key={key}
                    active={openPanels[key]}
                    icon={Icon}
                    label={label}
                    onClick={() => toggleFloatingPanel(key)}
                    pulsing={pulse}
                    theme={theme}
                  />
                ))}
              </div>

              <div className="pt-4 space-y-3">
                <div className="text-[10px] font-mono font-semibold uppercase tracking-wide px-2" style={{ color: `${theme.ink}30` }}>
                  Outils
                </div>
                <button
                  onClick={() => setFieldModalOpen(true)}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border text-[11px] font-mono tracking-wide transition-all"
                  style={{ borderColor: `${theme.accent}20`, color: `${theme.accent}70`, background: 'transparent' }}
                  onMouseEnter={e => { e.currentTarget.style.background = `${theme.accent}08`; e.currentTarget.style.color = theme.accent; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = `${theme.accent}70`; }}
                >
                  <Maximize2 size={13} />
                  <span>Champ Vivant</span>
                </button>
                {phase === 'finished' && (
                  <button
                    onClick={() => toggleShowDebrief(true)}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border text-[11px] font-mono tracking-wide transition-all"
                    style={{ borderColor: `${theme.accent}20`, color: `${theme.accent}70` }}
                    onMouseEnter={e => { e.currentTarget.style.background = `${theme.accent}08`; e.currentTarget.style.color = theme.accent; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = `${theme.accent}70`; }}
                  >
                    <FileText size={13} />
                    <span>Rapport final</span>
                  </button>
                )}
              </div>
            </div>

            {/* Panneaux ouverts */}
            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
              <AnimatePresence mode="popLayout">
                {openPanels.prediction && (
                  <PitchCard key="pred" theme={theme}>
                    <div className="px-4 pt-4 pb-0">
                      <SectionHeader label={V('prediction').label} icon={TrendingUp} theme={theme} />
                    </div>
                    <CommercialPredictionPanel theme={theme} V={V} />
                  </PitchCard>
                )}
                {openPanels.dynamics && (
                  <PitchCard key="dyna" theme={theme}>
                    <div className="px-4 pt-4 pb-0">
                      <SectionHeader label={V('dynamics').label} icon={Activity} theme={theme} />
                    </div>
                    <CommercialDynamicsPanel theme={theme} V={V} />
                  </PitchCard>
                )}
                {openPanels.causal && (
                  <PitchCard key="caus" theme={theme}>
                    <div className="px-4 pt-4 pb-0">
                      <SectionHeader label={V('causality').label} icon={Network} theme={theme} />
                    </div>
                    <CommercialCausalPanel theme={theme} V={V} />
                  </PitchCard>
                )}
                {openPanels.counterfactuals && phase === 'playing' && (
                  <PitchCard key="count" theme={theme}>
                    <div className="px-4 pt-4 pb-0">
                      <SectionHeader label={V('counterfactuals').label} icon={GitBranch} theme={theme} />
                    </div>
                    <CommercialCounterfactualsPanel counterfactuals={engine?.counterfactuals || []} theme={theme} V={V} />
                  </PitchCard>
                )}
                {openPanels.history && (
                  <PitchCard key="hist" theme={theme}>
                    <div className="px-4 pt-4 pb-0">
                      <SectionHeader label={V('history').label} icon={History} theme={theme} />
                    </div>
                    <CommercialHistoryPanel theme={theme} V={V} />
                  </PitchCard>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* ══ COLONNE CENTRALE — PESAField (inchangé) ══════════════════════ */}
          <div className="flex flex-col gap-6 overflow-hidden relative p-8 px-4">

            {/* Toggle LEFT */}
            <button
              onClick={() => setLeftCollapsed(v => !v)}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-5 h-14 rounded-r-lg border-y border-r backdrop-blur-md transition-all duration-200"
              style={{
                background: leftCollapsed ? theme.accent : `${theme.ink}08`,
                borderColor: leftCollapsed ? `${theme.accent}60` : `${theme.ink}12`,
                color: leftCollapsed ? '#fff' : `${theme.ink}30`,
              }}
            >
              {leftCollapsed ? <PanelLeftOpen size={12} /> : <PanelLeftClose size={12} />}
            </button>

            {/* Toggle RIGHT */}
            <button
              onClick={() => setRightCollapsed(v => !v)}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-5 h-14 rounded-l-lg border-y border-l backdrop-blur-md transition-all duration-200"
              style={{
                background: rightCollapsed ? theme.accent : `${theme.ink}08`,
                borderColor: rightCollapsed ? `${theme.accent}60` : `${theme.ink}12`,
                color: rightCollapsed ? '#fff' : `${theme.ink}30`,
              }}
            >
              {rightCollapsed ? <PanelRightOpen size={12} /> : <PanelRightClose size={12} />}
            </button>

            {/* Zone centrale — PESAFieldOverlay inchangé */}
            <div
              className="relative flex-1 overflow-hidden mx-5"
              style={{
                borderRadius: '3rem',
                border: `1px solid ${theme.panelBorder}`,
                background: themeKey === 'light' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                backdropFilter: 'blur(4px)',
                boxShadow: themeKey === 'light'
                  ? '0 8px 40px rgba(0,0,0,0.06)'
                  : '0 8px 40px rgba(0,0,0,0.4)',
              }}
            >
              <CommercialOverlay theme={theme} V={V} />
            </div>
          </div>

          {/* ══ COLONNE DROITE — ROI Panel ═══════════════════════════════════ */}
          <div
            className="flex flex-col gap-6 overflow-hidden"
            style={{
              opacity:       rightCollapsed ? 0 : 1,
              pointerEvents: rightCollapsed ? 'none' : 'auto',
              transition:    'opacity 200ms ease',
              padding:       rightCollapsed ? 0 : '2rem',
              paddingLeft:   rightCollapsed ? 0 : '1rem',
            }}
          >
            <div className="shrink-0">
              <ROIPanel theme={theme} V={V} scenario={scenario} />
            </div>
            <div className="shrink-0">
              <PitchCard theme={theme}>
                <div className="px-4 pt-4 pb-0">
                  <SectionHeader label={V('currentSolution').label} icon={CheckCircle2} theme={theme} />
                </div>
                <CommercialSolutionsPanel theme={theme} V={V} />
              </PitchCard>
            </div>
          </div>

        </main>

        {/* ── BOUTON RAPPORT FINAL ────────────────────────────────────────────── */}
        {phase === 'finished' && (
          <motion.button
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => toggleShowDebrief(true)}
            className="fixed bottom-12 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-full font-semibold flex items-center gap-4"
            style={{ background: theme.accent, color: '#fff', boxShadow: `0 8px 32px ${theme.accent}50` }}
          >
            <FileText size={20} className="animate-pulse" />
            CONSULTER LE RAPPORT FINAL
          </motion.button>
        )}

        {/* ── MODALE CHAMP VIVANT ─────────────────────────────────────────────── */}
        <LiveFieldModal isOpen={fieldModalOpen} onClose={() => setFieldModalOpen(false)} />

        {/* ── TOASTER ─────────────────────────────────────────────────────────── */}
        <Toaster position="bottom-right" />

        {/* ── DEBRIEF (RapportEnquete inchangé) ──────────────────────────────── */}
        {/* Import depuis GenericCase si le composant est séparé */}
        {showDebrief && typeof RapportEnquete !== 'undefined' && (
          <RapportEnquete onClose={closeDebrief} />
        )}

      </div>
    </div>
  );
}